// ═══════════════════════════════════════════════════════════════
// OpenGov EMC — App Router
// Single React app with client-side routing for all modules
// ═══════════════════════════════════════════════════════════════

import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import AppShell from './components/layout/AppShell';
import ProtectedRoute from './components/ui/ProtectedRoute';
import OfflineIndicator from './components/ui/OfflineIndicator';
import SessionTimeout from './components/ui/SessionTimeout';
import Login from './pages/auth/Login';
import Dashboard from './pages/dashboard/Dashboard';
import COP from './pages/dashboard/COP';
import { useAuthStore } from './store/authStore';
import { useThemeStore } from './store/themeStore';

// HotWash module
import HotWashLayout from './pages/hotwash/HotWashLayout';
import HotWashDashboard from './pages/hotwash/HotWashDashboard';
import Observations from './pages/hotwash/Observations';
import SubmitObservation from './pages/hotwash/SubmitObservation';
import HotWashAnalytics from './pages/hotwash/Analytics';
import HotWashIncidentDetail from './pages/hotwash/IncidentDetail';
import BulkImport from './pages/hotwash/BulkImport';

// Handoff module
import HandoffLayout from './pages/handoff/HandoffLayout';
import HandoffDashboard from './pages/handoff/HandoffDashboard';
import Events from './pages/handoff/Events';
import EventDetail from './pages/handoff/EventDetail';
import AllHandoffs from './pages/handoff/AllHandoffs';
import HandoffNoteDetail from './pages/handoff/HandoffDetail';
import ActionItems from './pages/handoff/ActionItems';
import HandoffAnalytics from './pages/handoff/HandoffAnalytics';
import Templates from './pages/handoff/Templates';
import HandoffNew from './pages/handoff/HandoffNew';
import StaffRoster from './pages/handoff/StaffRoster';

// RollCall module
import RollCallLayout from './pages/rollcall/RollCallLayout';
import RollCallDashboard from './pages/rollcall/RollCallDashboard';
import RCEvents from './pages/rollcall/Events';
import RCEventCreate from './pages/rollcall/EventCreate';
import RCEventDetail from './pages/rollcall/EventDetail';
import PersonnelList from './pages/rollcall/PersonnelList';
import PersonnelCreate from './pages/rollcall/PersonnelCreate';
import PersonnelEdit from './pages/rollcall/PersonnelEdit';
import RCCheckIn from './pages/rollcall/CheckIn';
import RCAnalytics from './pages/rollcall/Analytics';
import PersonnelBadges from './pages/rollcall/PersonnelBadges';
import PersonnelBulkImport from './pages/rollcall/PersonnelBulkImport';
import RCReports from './pages/rollcall/Reports';
import RCKioskMode from './pages/rollcall/KioskMode';

// GeoOps module (formerly Perimeter)
import GeoOpsLayout from './pages/geoops/GeoOpsLayout';
import GeoOpsDashboard from './pages/geoops/GeoOpsDashboard';
import GeoOpsMap from './pages/geoops/GeoOpsMap';
import ZoneDetail from './pages/geoops/ZoneDetail';
import ZoneOverview from './pages/geoops/ZoneOverview';
import GeoOpsDataSources from './pages/geoops/DataSources';

// Impact module
import ImpactLayout from './pages/impact/ImpactLayout';
import ImpactDashboard from './pages/impact/ImpactDashboard';
import AssessmentListPage from './pages/impact/AssessmentList';
import AssessmentDetailPage from './pages/impact/AssessmentDetail';
import QuickCapture from './pages/impact/QuickCapture';
import HomeAssessment from './pages/impact/HomeAssessment';
import InfraAssessment from './pages/impact/InfraAssessment';
import BulkEntryPage from './pages/impact/BulkEntry';
import DetailedCapture from './pages/impact/DetailedCapture';
import MapViewPage from './pages/impact/MapView';
import ImpactAnalytics from './pages/impact/ImpactAnalytics';
import ImpactReports from './pages/impact/Reports';
import SelfReport from './pages/impact/SelfReport';

// Intake module
import IntakeLayout from './pages/intake/IntakeLayout';
import IntakeDashboard from './pages/intake/IntakeDashboard';
import IntakeRegister from './pages/intake/Register';
import IntakeRoster from './pages/intake/Roster';
import IntakeDischarge from './pages/intake/Discharge';
import IntakeProperty from './pages/intake/Property';
import IntakeReports from './pages/intake/Reports';
import IntakePortal from './pages/intake/Portal';

// BattleRhythm module
import BRLayout from './pages/battlerhythm/BRLayout';
import BRDashboard from './pages/battlerhythm/BRDashboard';
import BRTemplates from './pages/battlerhythm/BRTemplates';
import ActivationDetail from './pages/battlerhythm/ActivationDetail';
import DisplayBoard from './pages/battlerhythm/DisplayBoard';

// Incident Hub module
import IncidentLayout from './pages/incidents/IncidentLayout';
import IncidentDashboard from './pages/incidents/IncidentDashboard';
import IncidentList from './pages/incidents/IncidentList';
import IncidentCreate from './pages/incidents/IncidentCreate';
import IncidentDetail from './pages/incidents/IncidentDetail';
import ActivityLog from './pages/incidents/ActivityLog';

// PACE module
import PaceLayout from './pages/pace/PaceLayout';
import PaceDashboard from './pages/pace/PaceDashboard';
import MyPace from './pages/pace/MyPace';
import PaceCallDowns from './pages/pace/CallDowns';
import PaceDirectory from './pages/pace/Directory';
import PacePlans from './pages/pace/Plans';
import PaceReports from './pages/pace/Reports';

// Blackboard module
import BlackboardLayout from './pages/blackboard/BlackboardLayout';
import BBDashboard from './pages/blackboard/Dashboard';
import BBMyTraining from './pages/blackboard/MyTraining';
import BBClassrooms from './pages/blackboard/Classrooms';
import BBTrainingLibrary from './pages/blackboard/TrainingLibrary';
import BBTrainingRecords from './pages/blackboard/TrainingRecords';
// BBUserManagement removed — Blackboard now uses central Ignatius user accounts
import BBTrainingPlan from './pages/blackboard/TrainingPlan';
import BBAnalytics from './pages/blackboard/Analytics';
import BBPositionPlans from './pages/blackboard/PositionPlans';
import BBNotifications from './pages/blackboard/Notifications';
import BBComms from './pages/blackboard/Comms';
import BBReports from './pages/blackboard/Reports';
import BBVerifyCertificate from './pages/blackboard/VerifyCertificate';
import BBCertificates from './pages/blackboard/Certificates';

// VolunteerOS module
import VolunteerLayout from './pages/volunteer/VolunteerLayout';
import VolDashboard from './pages/volunteer/Dashboard';
import VolEvents from './pages/volunteer/Events';
import VolSchedule from './pages/volunteer/Schedule';
import VolCheckIn from './pages/volunteer/CheckIn';
import VolRoster from './pages/volunteer/Roster';
import VolEventMap from './pages/volunteer/EventMap';
import VolScheduleBuilder from './pages/volunteer/ScheduleBuilder';
import VolReports from './pages/volunteer/Reports';
import VolComms from './pages/volunteer/Comms';
import VolSettings from './pages/volunteer/Settings';
import VolunteerPublicSignup from './pages/volunteer/PublicSignup';

// MainStreet module
import MainStreetLayout from './pages/mainstreet/MainStreetLayout';
import MSCheckpoint from './pages/mainstreet/Checkpoint';
import MSDashboard from './pages/mainstreet/Dashboard';
import MSOrganizations from './pages/mainstreet/Organizations';
import MSOrgDetail from './pages/mainstreet/OrgDetail';
import MSOrgCreate from './pages/mainstreet/OrgCreate';
import MSFacilities from './pages/mainstreet/Facilities';
import MSContacts from './pages/mainstreet/Contacts';
import MSStatusChecks from './pages/mainstreet/StatusChecks';
import MSDamageReports from './pages/mainstreet/DamageReports';
import MSResources from './pages/mainstreet/Resources';
import MSLists from './pages/mainstreet/Lists';
import MSReentry from './pages/mainstreet/Reentry';
import MSFeed from './pages/mainstreet/Feed';
import MSAnalytics from './pages/mainstreet/Analytics';
import MSReports from './pages/mainstreet/Reports';
import MSBusinessMap from './pages/mainstreet/BusinessMap';
import MSVerifyPass from './pages/mainstreet/VerifyPass';
import MSPublicBusinessSignup from './pages/mainstreet/PublicBusinessSignup';
import MSMyBusiness from './pages/mainstreet/MyBusiness';

// File Library module
import FileLibraryLayout from './pages/filelibrary/FileLibraryLayout';
import FLDashboard from './pages/filelibrary/Dashboard';
import FLBrowse from './pages/filelibrary/Browse';
import FLSearch from './pages/filelibrary/Search';
import FLPublish from './pages/filelibrary/Publish';
import FLCategories from './pages/filelibrary/Categories';
import FLDocDetail from './pages/filelibrary/DocDetail';

// Debris ADMS module
import DebrisLayout from './pages/debris/DebrisLayout';
import DebrisDashboard from './pages/debris/DebrisDashboard';
import DebrisZones from './pages/debris/Zones';
import DebrisContractors from './pages/debris/Contractors';
import DebrisCrews from './pages/debris/Crews';
import DebrisTrucks from './pages/debris/Trucks';
import DebrisLoadTickets from './pages/debris/LoadTickets';
import DebrisDisposalSites from './pages/debris/DisposalSites';
import DebrisInvoices from './pages/debris/Invoices';
import DebrisDocuments from './pages/debris/Documents';
import DebrisAnalytics from './pages/debris/Analytics';
import DebrisDrivers from './pages/debris/Drivers';
import DebrisMap from './pages/debris/DebrisMap';
import DebrisRoutePlanner from './pages/debris/RoutePlanner';
import DebrisReports from './pages/debris/Reports';
import DebrisImport from './pages/debris/Import';
import DebrisMonitorStation from './pages/debris/MonitorStation';
import DebrisRateSchedule from './pages/debris/RateSchedule';
import DebrisRightOfEntry from './pages/debris/RightOfEntry';
import DebrisMonitors from './pages/debris/Monitors';
import DebrisFieldPickup from './pages/debris/FieldPickup';
import DebrisTicketVerify from './pages/debris/TicketVerify';
import DebrisPhotos from './pages/debris/Photos';
import DebrisRequests from './pages/debris/Requests';

// Shelter Management module
import ShelterLayout from './pages/shelter/ShelterLayout';
import ShelterDashboard from './pages/shelter/ShelterDashboard';
import SheltersList from './pages/shelter/Shelters';
import ShelterIntake from './pages/shelter/Intake';
import ShelterSurvivors from './pages/shelter/Survivors';
import ShelterSurvivorDetail from './pages/shelter/SurvivorDetail';
import ShelterFamilies from './pages/shelter/Families';
import ShelterPets from './pages/shelter/Pets';
import ShelterVehicles from './pages/shelter/Vehicles';
import ShelterLookup from './pages/shelter/Lookup';
import ShelterMedical from './pages/shelter/Medical';
import ShelterTransfers from './pages/shelter/Transfers';
import ShelterPropertyPage from './pages/shelter/Property';
import ShelterSupplies from './pages/shelter/Supplies';
import ShelterTriageSetup from './pages/shelter/TriageSetup';
import ShelterIncidents from './pages/shelter/Incidents';
import ShelterReferrals from './pages/shelter/Referrals';
import ShelterReunification from './pages/shelter/Reunification';
import ShelterComms from './pages/shelter/Comms';
import ShelterMap from './pages/shelter/ShelterMap';
import ShelterAnalytics from './pages/shelter/Analytics';
import ShelterFacilityLibrary from './pages/shelter/FacilityLibrary';
import ShelterDistribution from './pages/shelter/SupplyDistribution';
import ShelterPreRegister from './pages/shelter/PublicPreRegister';
import PublicReunification from './pages/shelter/PublicReunification';
import ShelterReports from './pages/shelter/Reports';

// Disaster Grants module
import ProgramSelector from './pages/pagrants/ProgramSelector';
import PAGrantsLayout from './pages/pagrants/PAGrantsLayout';
import PAGrantsDashboard from './pages/pagrants/PAGrantsDashboard';
import PADisasters from './pages/pagrants/Disasters';
import PAApplicants from './pages/pagrants/Applicants';
import PAApplicantDetail from './pages/pagrants/ApplicantDetail';
import PAProjectWorkspace from './pages/pagrants/ProjectWorkspace';
import PAFemaForms from './pages/pagrants/FemaForms';
import PAReports from './pages/pagrants/Reports';
import PAAnalytics from './pages/pagrants/Analytics';
import PAMap from './pages/pagrants/PAMap';
import PAProjects from './pages/pagrants/Projects';
import PADamageInventory from './pages/pagrants/DamageInventory';
import PADocuments from './pages/pagrants/Documents';
import PATimeExtensions from './pages/pagrants/TimeExtensions';
import PAReimbursements from './pages/pagrants/Reimbursements';
import PAKanban from './pages/pagrants/PAKanban';
import PAChecklistSettings from './pages/pagrants/ChecklistSettings';
import HMGPLayout from './pages/pagrants/hmgp/HMGPLayout';
import HMGPDashboard from './pages/pagrants/hmgp/HMGPDashboard';
import HMGPProjects from './pages/pagrants/hmgp/HMGPProjects';
import HMGPSubapplications from './pages/pagrants/hmgp/HMGPSubapplications';
import HMGPProjectWorkspace from './pages/pagrants/hmgp/HMGPProjectWorkspace';
import HMGPMap from './pages/pagrants/hmgp/HMGPMap';
import HMGPReports from './pages/pagrants/hmgp/HMGPReports';
import HMGPAnalytics from './pages/pagrants/hmgp/HMGPAnalytics';
import HMGPReimbursements from './pages/pagrants/hmgp/HMGPReimbursements';
import HMGPPriorityScoring from './pages/pagrants/hmgp/HMGPPriorityScoring';
import HMGPFemaForms from './pages/pagrants/hmgp/HMGPFemaForms';
import HMGPQuarterlyReports from './pages/pagrants/hmgp/HMGPQuarterlyReports';

// Time & Labor module
import TimeLaborLayout from './pages/timelabor/TimeLaborLayout';
import TimeLaborDashboard from './pages/timelabor/TimeLaborDashboard';
import EmployeeMaster from './pages/timelabor/EmployeeMaster';
import TimeEntry from './pages/timelabor/TimeEntry';
import TimeLaborSummaries from './pages/timelabor/Summaries';
import TimeLaborReports from './pages/timelabor/Reports';
import FF104Generator from './pages/timelabor/FF104Generator';
import SupervisorReview from './pages/timelabor/SupervisorReview';
import TimeLaborBulkImport from './pages/timelabor/BulkImport';
import CodeConfig from './pages/timelabor/CodeConfig';

// Workflows module
import WorkflowLayout from './pages/workflows/WorkflowLayout';
import WorkflowInbox from './pages/workflows/WorkflowInbox';
import WorkflowActive from './pages/workflows/WorkflowActive';
import WorkflowAll from './pages/workflows/WorkflowAll';
import WorkflowTemplates from './pages/workflows/WorkflowTemplates';
import WorkflowInstanceDetail from './pages/workflows/WorkflowInstanceDetail';

// Forms module
import FormsLayout from './pages/forms/FormsLayout';
import FormsDashboard from './pages/forms/FormsDashboard';
import FormTemplatesList from './pages/forms/FormTemplates';
import FormBuilder from './pages/forms/FormBuilder';
import FormResponses from './pages/forms/FormResponses';
import FormPreview from './pages/forms/FormPreview';
import FormPublic from './pages/forms/FormPublic';
import FormPublished from './pages/forms/FormPublished';

// Settings module (RBAC)
import SettingsLayout from './pages/settings/SettingsLayout';
import RolesTab from './pages/settings/RolesTab';
import UsersTab from './pages/settings/UsersTab';
import ActivityLogTab from './pages/settings/ActivityLogTab';


export default function App() {
  const { restoreSession, isAuthenticated, loadIncidents } = useAuthStore();
  const { theme } = useThemeStore();

  // Set theme on mount
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Restore session on mount
  useEffect(() => {
    const restored = restoreSession();
    if (restored) {
      loadIncidents();
    }
  }, []);

  return (
    <BrowserRouter>
      <OfflineIndicator />
      <SessionTimeout />
      <Routes>
        {/* Public */}
        <Route path="/login" element={
          isAuthenticated ? <Navigate to="/" replace /> : <Login />
        } />

        {/* Protected — inside AppShell */}
        <Route element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }>
          <Route path="/" element={<Dashboard />} />
          <Route path="/cop" element={<COP />} />

          {/* Module routes — placeholders until migrated */}
          <Route path="/hotwash" element={<HotWashLayout />}>
            <Route index element={<HotWashDashboard />} />
            <Route path="observations" element={<Observations />} />
            <Route path="submit" element={<SubmitObservation />} />
            <Route path="analytics" element={<HotWashAnalytics />} />
            <Route path="import" element={<BulkImport />} />
            <Route path="incident/:id" element={<HotWashIncidentDetail />} />
          </Route>
          <Route path="/handoff" element={<HandoffLayout />}>
            <Route index element={<HandoffDashboard />} />
            <Route path="events" element={<Events />} />
            <Route path="event/:id" element={<EventDetail />} />
            <Route path="notes" element={<AllHandoffs />} />
            <Route path="note/:id" element={<HandoffNoteDetail />} />
            <Route path="staff" element={<StaffRoster />} />
            <Route path="actions" element={<ActionItems />} />
            <Route path="analytics" element={<HandoffAnalytics />} />
            <Route path="templates" element={<Templates />} />
            <Route path="new" element={<HandoffNew />} />
            <Route path="event/:eventId/handoff/new" element={<HandoffNew />} />
          </Route>
          <Route path="/rollcall" element={<RollCallLayout />}>
            <Route index element={<RollCallDashboard />} />
            <Route path="events" element={<RCEvents />} />
            <Route path="events/new" element={<RCEventCreate />} />
            <Route path="event/:id" element={<RCEventDetail />} />
            <Route path="personnel" element={<PersonnelList />} />
            <Route path="personnel/new" element={<PersonnelCreate />} />
            <Route path="personnel/:id/edit" element={<PersonnelEdit />} />
            <Route path="personnel/badges" element={<PersonnelBadges />} />
            <Route path="personnel/import" element={<PersonnelBulkImport />} />
            <Route path="checkin" element={<RCCheckIn />} />
            <Route path="analytics" element={<RCAnalytics />} />
            <Route path="reports" element={<RCReports />} />
          </Route>
          <Route path="/geoops" element={<GeoOpsLayout />}>
            <Route index element={<GeoOpsDashboard />} />
            <Route path="map" element={<GeoOpsMap />} />
            <Route path="zone/:id" element={<ZoneDetail />} />
            <Route path="overview" element={<ZoneOverview />} />
            <Route path="data-sources" element={<GeoOpsDataSources />} />
          </Route>
          <Route path="/perimeter" element={<Navigate to="/geoops" replace />} />
          <Route path="/impact" element={<ImpactLayout />}>
            <Route index element={<ImpactDashboard />} />
            <Route path="assessments" element={<AssessmentListPage />} />
            <Route path="assessment/:id" element={<AssessmentDetailPage />} />
            <Route path="capture" element={<QuickCapture />} />
            <Route path="home" element={<HomeAssessment />} />
            <Route path="infra" element={<InfraAssessment />} />
            <Route path="bulk" element={<BulkEntryPage />} />
            <Route path="detailed" element={<DetailedCapture />} />
            <Route path="map" element={<MapViewPage />} />
            <Route path="analytics" element={<ImpactAnalytics />} />
            <Route path="reports" element={<ImpactReports />} />
          </Route>
          <Route path="/intake" element={<IntakeLayout />}>
            <Route index element={<IntakeDashboard />} />
            <Route path="register" element={<IntakeRegister />} />
            <Route path="roster" element={<IntakeRoster />} />
            <Route path="discharge" element={<IntakeDischarge />} />
            <Route path="property" element={<IntakeProperty />} />
            <Route path="reports" element={<IntakeReports />} />
          </Route>
          <Route path="/battlerhythm" element={<BRLayout />}>
            <Route index element={<BRDashboard />} />
            <Route path="templates" element={<BRTemplates />} />
            <Route path="activation/:id" element={<ActivationDetail />} />
            <Route path="display" element={<DisplayBoard />} />
          </Route>
          <Route path="/pace" element={<PaceLayout />}>
            <Route index element={<PaceDashboard />} />
            <Route path="my-pace" element={<MyPace />} />
            <Route path="directory" element={<PaceDirectory />} />
            <Route path="calldowns" element={<PaceCallDowns />} />
            <Route path="plans" element={<PacePlans />} />
            <Route path="reports" element={<PaceReports />} />
          </Route>
          <Route path="/volunteer" element={<VolunteerLayout />}>
            <Route index element={<VolDashboard />} />
            <Route path="events" element={<VolEvents />} />
            <Route path="schedule" element={<VolSchedule />} />
            <Route path="checkin" element={<VolCheckIn />} />
            <Route path="roster" element={<VolRoster />} />
            <Route path="map" element={<VolEventMap />} />
            <Route path="scheduler" element={<VolScheduleBuilder />} />
            <Route path="reports" element={<VolReports />} />
            <Route path="comms" element={<VolComms />} />
            <Route path="settings" element={<VolSettings />} />
          </Route>
          <Route path="/mainstreet" element={<MainStreetLayout />}>
            <Route index element={<MSDashboard />} />
            <Route path="my-business" element={<MSMyBusiness />} />
            <Route path="organizations" element={<MSOrganizations />} />
            <Route path="org/new" element={<MSOrgCreate />} />
            <Route path="org/:id" element={<MSOrgDetail />} />
            <Route path="facilities" element={<MSFacilities />} />
            <Route path="contacts" element={<MSContacts />} />
            <Route path="status" element={<MSStatusChecks />} />
            <Route path="damage" element={<MSDamageReports />} />
            <Route path="resources" element={<MSResources />} />
            <Route path="lists" element={<MSLists />} />
            <Route path="reentry" element={<MSReentry />} />
            <Route path="feed" element={<MSFeed />} />
            <Route path="analytics" element={<MSAnalytics />} />
            <Route path="reports" element={<MSReports />} />
            <Route path="map" element={<MSBusinessMap />} />
            <Route path="checkpoint" element={<MSCheckpoint />} />
          </Route>
          <Route path="/filelibrary" element={<FileLibraryLayout />}>
            <Route index element={<FLDashboard />} />
            <Route path="browse" element={<FLBrowse />} />
            <Route path="search" element={<FLSearch />} />
            <Route path="publish" element={<FLPublish />} />
            <Route path="publish/:id" element={<FLPublish />} />
            <Route path="categories" element={<FLCategories />} />
            <Route path="doc/:id" element={<FLDocDetail />} />
          </Route>
          <Route path="/blackboard" element={<BlackboardLayout />}>
            <Route index element={<BBDashboard />} />
            <Route path="my-training" element={<BBMyTraining />} />
            <Route path="classrooms" element={<BBClassrooms />} />
            <Route path="library" element={<BBTrainingLibrary />} />
            <Route path="records" element={<BBTrainingRecords />} />
            {/* Users managed centrally via Settings > Users */}
            <Route path="plan/:userId" element={<BBTrainingPlan />} />
            <Route path="analytics" element={<BBAnalytics />} />
            <Route path="positions" element={<BBPositionPlans />} />
            <Route path="certificates" element={<BBCertificates />} />
            <Route path="reports" element={<BBReports />} />
            <Route path="comms" element={<BBComms />} />
            <Route path="notifications" element={<BBNotifications />} />
          </Route>

          {/* Incident Hub */}
          <Route path="/incidents" element={<IncidentLayout />}>
            <Route index element={<IncidentDashboard />} />
            <Route path="list" element={<IncidentList />} />
            <Route path="new" element={<IncidentCreate />} />
            <Route path="activity" element={<ActivityLog />} />
            <Route path=":id" element={<IncidentDetail />} />
            <Route path=":id/edit" element={<IncidentCreate />} />
          </Route>

          {/* Debris ADMS */}
          <Route path="/debris" element={<DebrisLayout />}>
            <Route index element={<DebrisDashboard />} />
            <Route path="zones" element={<DebrisZones />} />
            <Route path="contractors" element={<DebrisContractors />} />
            <Route path="crews" element={<DebrisCrews />} />
            <Route path="trucks" element={<DebrisTrucks />} />
            <Route path="tickets" element={<DebrisLoadTickets />} />
            <Route path="sites" element={<DebrisDisposalSites />} />
            <Route path="invoices" element={<DebrisInvoices />} />
            <Route path="documents" element={<DebrisDocuments />} />
            <Route path="analytics" element={<DebrisAnalytics />} />
            <Route path="drivers" element={<DebrisDrivers />} />
            <Route path="map" element={<DebrisMap />} />
            <Route path="routes" element={<DebrisRoutePlanner />} />
            <Route path="reports" element={<DebrisReports />} />
            <Route path="import" element={<DebrisImport />} />
            <Route path="monitor" element={<DebrisMonitorStation />} />
            <Route path="monitors" element={<DebrisMonitors />} />
            <Route path="rates" element={<DebrisRateSchedule />} />
            <Route path="roe" element={<DebrisRightOfEntry />} />
            <Route path="pickup" element={<DebrisFieldPickup />} />
            <Route path="photos" element={<DebrisPhotos />} />
            <Route path="requests" element={<DebrisRequests />} />
          </Route>

          {/* Shelter Management */}
          <Route path="/shelter" element={<ShelterLayout />}>
            <Route index element={<ShelterDashboard />} />
            <Route path="shelters" element={<SheltersList />} />
            <Route path="intake" element={<ShelterIntake />} />
            <Route path="survivors" element={<ShelterSurvivors />} />
            <Route path="survivor/:id" element={<ShelterSurvivorDetail />} />
            <Route path="families" element={<ShelterFamilies />} />
            <Route path="pets" element={<ShelterPets />} />
            <Route path="vehicles" element={<ShelterVehicles />} />
            <Route path="lookup" element={<ShelterLookup />} />
            <Route path="medical" element={<ShelterMedical />} />
            <Route path="transfers" element={<ShelterTransfers />} />
            <Route path="property" element={<ShelterPropertyPage />} />
            <Route path="supplies" element={<ShelterSupplies />} />
            <Route path="distribution" element={<ShelterDistribution />} />
            <Route path="triage" element={<ShelterTriageSetup />} />
            <Route path="incidents" element={<ShelterIncidents />} />
            <Route path="referrals" element={<ShelterReferrals />} />
            <Route path="reunification" element={<ShelterReunification />} />
            <Route path="comms" element={<ShelterComms />} />
            <Route path="map" element={<ShelterMap />} />
            <Route path="reports" element={<ShelterReports />} />
            <Route path="facility-library" element={<ShelterFacilityLibrary />} />
            <Route path="analytics" element={<ShelterAnalytics />} />
          </Route>

          {/* Disaster Grants — Program selector + PA and HMGP sub-modules */}
          <Route path="/pagrants" element={<ProgramSelector />} />
          <Route path="/pagrants/pa" element={<PAGrantsLayout />}>
            <Route index element={<PAGrantsDashboard />} />
            <Route path="projects" element={<PAProjects />} />
            <Route path="disasters" element={<PADisasters />} />
            <Route path="applicants" element={<PAApplicants />} />
            <Route path="applicants/:orgId" element={<PAApplicantDetail />} />
            <Route path="applicants/:orgId/projects/:projectId" element={<PAProjectWorkspace />} />
            <Route path="damage" element={<PADamageInventory />} />
            <Route path="documents" element={<PADocuments />} />
            <Route path="forms" element={<PAFemaForms />} />
            <Route path="reports" element={<PAReports />} />
            <Route path="analytics" element={<PAAnalytics />} />
            <Route path="map" element={<PAMap />} />
            <Route path="projects/:projectId" element={<PAProjectWorkspace />} />
            <Route path="time-extensions" element={<PATimeExtensions />} />
            <Route path="reimbursements" element={<PAReimbursements />} />
            <Route path="pipeline" element={<PAKanban />} />
            <Route path="settings/checklist" element={<PAChecklistSettings />} />
            <Route path="*" element={null} />
          </Route>
          <Route path="/pagrants/hmgp" element={<HMGPLayout />}>
            <Route index element={<HMGPDashboard />} />
            <Route path="subapplications" element={<HMGPSubapplications />} />
            <Route path="projects" element={<HMGPProjects />} />
            <Route path="projects/:projectId" element={<HMGPProjectWorkspace />} />
            <Route path="applicants" element={<PAApplicants />} />
            <Route path="applicants/:orgId" element={<PAApplicantDetail />} />
            <Route path="applicants/:orgId/projects/:projectId" element={<HMGPProjectWorkspace />} />
            <Route path="disasters" element={<PADisasters />} />
            <Route path="map" element={<HMGPMap />} />
            <Route path="reimbursements" element={<HMGPReimbursements />} />
            <Route path="reports" element={<HMGPReports />} />
            <Route path="analytics" element={<HMGPAnalytics />} />
            <Route path="forms" element={<HMGPFemaForms />} />
            <Route path="scoring" element={<HMGPPriorityScoring />} />
            <Route path="quarterly" element={<HMGPQuarterlyReports />} />
            <Route path="settings/checklist" element={<PAChecklistSettings />} />
          </Route>

          {/* Time & Labor */}
          <Route path="/timelabor" element={<TimeLaborLayout />}>
            <Route index element={<TimeLaborDashboard />} />
            <Route path="timeentry" element={<TimeEntry />} />
            <Route path="review" element={<SupervisorReview />} />
            <Route path="import" element={<TimeLaborBulkImport />} />
            <Route path="employees" element={<EmployeeMaster />} />
            <Route path="summaries" element={<TimeLaborSummaries />} />
            <Route path="reports" element={<TimeLaborReports />} />
            <Route path="ff104" element={<FF104Generator />} />
            <Route path="settings" element={<CodeConfig />} />
          </Route>

          {/* Workflows */}
          <Route path="/workflows" element={<WorkflowLayout />}>
            <Route index element={<WorkflowInbox />} />
            <Route path="active" element={<WorkflowActive />} />
            <Route path="all" element={<WorkflowAll />} />
            <Route path="templates" element={<WorkflowTemplates />} />
            <Route path="instance/:instanceId" element={<WorkflowInstanceDetail />} />
          </Route>

          {/* Forms */}
          <Route path="/forms" element={<FormsLayout />}>
            <Route index element={<FormsDashboard />} />
            <Route path="published" element={<FormPublished />} />
            <Route path="templates" element={<FormTemplatesList />} />
            <Route path="builder" element={<FormBuilder />} />
            <Route path="builder/:id" element={<FormBuilder />} />
            <Route path="responses" element={<FormResponses />} />
            <Route path="preview/:id" element={<FormPreview />} />
          </Route>

          {/* Settings (RBAC) */}
          <Route path="/settings" element={<SettingsLayout />}>
            <Route index element={<RolesTab />} />
            <Route path="users" element={<UsersTab />} />
            <Route path="activity" element={<ActivityLogTab />} />
          </Route>
        </Route>

        {/* Self-Report — public, outside AppShell */}
        <Route path="/self-report/:incidentId" element={<SelfReport />} />

        {/* Intake Portal — public family lookup, outside AppShell */}
        <Route path="/portal/:incidentId" element={<IntakePortal />} />

        {/* RollCall Kiosk — full-screen, outside AppShell */}
        <Route path="/rollcall/kiosk" element={
          <ProtectedRoute><RCKioskMode /></ProtectedRoute>
        } />

        {/* Volunteer Public Signup — outside AppShell */}
        <Route path="/volunteer/signup" element={<VolunteerPublicSignup />} />
        <Route path="/volunteer/signup/:incidentId" element={<VolunteerPublicSignup />} />

        {/* MainStreet Re-entry Pass Verification — public */}
        <Route path="/mainstreet/verify/:code" element={<MSVerifyPass />} />
        <Route path="/mainstreet/register" element={<MSPublicBusinessSignup />} />
        <Route path="/forms/public/:slug" element={<FormPublic />} />
        <Route path="/shelter/pre-register" element={<ShelterPreRegister />} />
        <Route path="/shelter/find-person" element={<PublicReunification />} />

        {/* Debris — Ticket Verification (public, scanned from QR) */}
        <Route path="/debris/verify/:ticketId" element={<DebrisTicketVerify />} />

        {/* Certificate Verification — public */}
        <Route path="/verify/:certCode" element={<BBVerifyCertificate />} />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
